import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { Select } from "../select";
import { Text } from "../text";
import { Badge } from "../badge";
import { Textarea } from "../textarea";
import { Button } from "../button";

const statusLabels = {
  open: "Abierto",
  closed: "Cerrado",
  needs_attention: "En Atención",
};

function formatDate(ts) {
  if (!ts) return "-";
  const date = ts.toDate
    ? ts.toDate()
    : new Date(ts.seconds ? ts.seconds * 1000 : ts);
  return date.toLocaleString();
}

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [commentInputs, setCommentInputs] = useState({});

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const ticketsRef = collection(db, "ticketsUsuarios");
        const snapshot = await getDocs(ticketsRef);
        setTickets(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setTickets([]);
      }
      setLoading(false);
    };
    fetchTickets();
  }, []);

  // Unique users for filter dropdown
  const users = Array.from(new Set(tickets.map((t) => t.userEmail))).filter(
    Boolean
  );

  // Filtered tickets
  const filteredTickets = tickets.filter((t) => {
    const statusMatch = statusFilter === "all" || t.status === statusFilter;
    const userMatch = !userFilter || t.userEmail === userFilter;
    return statusMatch && userMatch;
  });

  const handleStatusChange = async (ticketId, newStatus) => {
    await updateDoc(doc(db, "ticketsUsuarios", ticketId), {
      status: newStatus,
      updatedAt: new Date(),
    });
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t))
    );
  };

  const handleCommentChange = (ticketId, value) => {
    setCommentInputs((prev) => ({ ...prev, [ticketId]: value }));
  };

  const handleCommentSave = async (ticketId) => {
    const comment = commentInputs[ticketId] || "";
    await updateDoc(doc(db, "ticketsUsuarios", ticketId), {
      adminComment: comment,
      updatedAt: new Date(),
    });
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, adminComment: comment } : t))
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4 max-lg:px-0">
      <Text as="h2" className="text-2xl font-bold mb-4">
        Todos los Tickets de Soporte
      </Text>
      <div className="flex gap-4 mb-4">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          <option value="open">Abierto</option>
          <option value="needs_attention">En Atención</option>
          <option value="closed">Cerrado</option>
        </Select>
        <Select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        >
          <option value="">Todos los usuarios</option>
          {users.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </Select>
      </div>
      {loading ? (
        <Text>Cargando...</Text>
      ) : filteredTickets.length === 0 ? (
        <Text className="text-gray-500">No hay tickets.</Text>
      ) : (
        <table className="min-w-full bg-white border rounded">
          <thead>
            <tr>
              <th className="p-2 border">Usuario</th>
              <th className="p-2 border">Asunto</th>
              <th className="p-2 border">Mensaje</th>
              <th className="p-2 border">Estado</th>
              <th className="p-2 border">Creado</th>
              <th className="p-2 border">Última actualización</th>
              <th className="p-2 border">Acciones</th>
              <th className="p-2 border">Comentarios</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.map((ticket) => (
              <tr key={ticket.id}>
                <td className="p-2 border">
                  {ticket.userName} <br />{" "}
                  <span className="text-xs text-gray-500">
                    {ticket.userEmail}
                  </span>
                </td>
                <td className="p-2 border">{ticket.subject}</td>
                <td className="p-2 border">{ticket.message}</td>
                <td className="p-2 border">
                  <Badge
                    color={
                      ticket.status === "closed"
                        ? "green"
                        : ticket.status === "needs_attention"
                        ? "amber"
                        : "blue"
                    }
                  >
                    {statusLabels[ticket.status] || ticket.status}
                  </Badge>
                </td>
                <td className="p-2 border text-xs text-gray-500">
                  {formatDate(ticket.createdAt)}
                </td>
                <td className="p-2 border text-xs text-gray-500">
                  {formatDate(ticket.updatedAt)}
                </td>
                <td className="p-2 border">
                  <Select
                    value={ticket.status}
                    onChange={(e) =>
                      handleStatusChange(ticket.id, e.target.value)
                    }
                  >
                    <option value="open">Abierto</option>
                    <option value="needs_attention">Necesita Atención</option>
                    <option value="closed">Cerrado</option>
                  </Select>
                </td>
                <td className="p-2 border min-w-[220px]">
                  {ticket.adminComment && (
                    <div className="mb-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded text-blue-800 text-sm">
                      <strong>Comentario actual:</strong> {ticket.adminComment}
                    </div>
                  )}
                  <Textarea
                    value={
                      commentInputs[ticket.id] ?? ticket.adminComment ?? ""
                    }
                    onChange={(e) =>
                      handleCommentChange(ticket.id, e.target.value)
                    }
                    placeholder="Agregar o editar comentario..."
                    rows={2}
                  />
                  <Button
                    className="mt-1"
                    size="sm"
                    onClick={() => handleCommentSave(ticket.id)}
                    disabled={commentInputs[ticket.id] === ticket.adminComment}
                  >
                    Guardar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminTickets;
